import { useCallback, useEffect, useRef, useState } from 'react';
import './PassportIdleVideo.css';

const LOOP_LEAD_SEC = 0.2;
const WATCHDOG_MS = 4000;
const STALL_RETRY_MS = 1500;

/**
 * Full-bleed looping promo video while Passport camera scan is off.
 * Handles WebView stalls, unexpected pause, and end-of-file without flashing empty frames.
 */
export default function PassportIdleVideo({ src, visible, mediaKey = '', onRecover }) {
  const videoRef = useRef(null);
  const srcRef = useRef('');
  const mediaKeyRef = useRef('');
  const stallTimerRef = useRef(null);
  const watchdogRef = useRef(null);
  const [showFrame, setShowFrame] = useState(false);

  const playFromStart = useCallback((el) => {
    if (!el) return;
    el.muted = true;
    try {
      if (el.ended || (Number.isFinite(el.duration) && el.currentTime >= el.duration - 0.05)) {
        el.currentTime = 0;
      }
    } catch {
      /* ignore */
    }
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  const ensurePlaying = useCallback(() => {
    const el = videoRef.current;
    if (!el || !visible || !srcRef.current) return;
    if (el.readyState >= 2) {
      playFromStart(el);
      return;
    }
    try {
      el.load();
    } catch {
      /* ignore */
    }
    playFromStart(el);
  }, [playFromStart, visible]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    const clearTimers = () => {
      if (stallTimerRef.current) {
        window.clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
      if (watchdogRef.current) {
        window.clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
    };

    if (!visible || !src) {
      setShowFrame(false);
      clearTimers();
      try {
        el.pause();
      } catch {
        /* ignore */
      }
      return undefined;
    }

    const scheduleStallRetry = () => {
      if (stallTimerRef.current) window.clearTimeout(stallTimerRef.current);
      stallTimerRef.current = window.setTimeout(() => {
        stallTimerRef.current = null;
        ensurePlaying();
      }, STALL_RETRY_MS);
    };

    const onPlaying = () => {
      setShowFrame(true);
      if (stallTimerRef.current) {
        window.clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };
    const onWaiting = () => scheduleStallRetry();
    const onStalled = () => scheduleStallRetry();
    const onEnded = () => playFromStart(el);
    const onError = () => {
      const key = mediaKeyRef.current;
      const url = srcRef.current;
      if (!url) return;
      setShowFrame(false);
      try {
        el.pause();
        el.removeAttribute('src');
        el.load();
        el.src = url;
        el.load();
      } catch {
        /* ignore */
      }
      mediaKeyRef.current = key;
      playFromStart(el);
      if (typeof onRecover === 'function') {
        window.setTimeout(() => {
          try {
            onRecover();
          } catch {
            /* ignore */
          }
        }, 400);
      }
    };
    const onPause = () => {
      if (!visible || document.visibilityState === 'hidden') return;
      window.setTimeout(() => {
        if (!videoRef.current || videoRef.current !== el) return;
        if (el.paused && !el.ended) playFromStart(el);
      }, 120);
    };
    const onTimeUpdate = () => {
      const dur = el.duration;
      if (!Number.isFinite(dur) || dur <= LOOP_LEAD_SEC * 2) return;
      if (el.currentTime >= dur - LOOP_LEAD_SEC) {
        try {
          el.currentTime = LOOP_LEAD_SEC;
        } catch {
          onEnded();
        }
      }
    };

    el.addEventListener('playing', onPlaying);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('stalled', onStalled);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    el.addEventListener('pause', onPause);
    el.addEventListener('timeupdate', onTimeUpdate);

    const keyChanged = String(mediaKey || '').trim() !== mediaKeyRef.current;
    const srcChanged = src !== srcRef.current;
    srcRef.current = src;
    mediaKeyRef.current = String(mediaKey || '').trim();

    if (keyChanged || srcChanged || !el.src) {
      setShowFrame(false);
      try {
        el.pause();
        el.src = src;
        el.load();
      } catch {
        /* ignore */
      }
    }

    playFromStart(el);

    watchdogRef.current = window.setInterval(() => {
      if (!visible || document.visibilityState === 'hidden') return;
      const node = videoRef.current;
      if (!node || node !== el) return;
      if (node.paused || node.ended || node.readyState < 2) {
        ensurePlaying();
      }
    }, WATCHDOG_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') ensurePlaying();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimers();
      document.removeEventListener('visibilitychange', onVisibility);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('stalled', onStalled);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('timeupdate', onTimeUpdate);
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    };
  }, [src, visible, mediaKey, ensurePlaying, playFromStart, onRecover]);

  if (!src || !visible) return null;

  return (
    <div className="passport-idle-video" aria-hidden="true">
      <video
        ref={videoRef}
        className={`passport-idle-video__media${showFrame ? '' : ' passport-idle-video__media--preparing'}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
      />
    </div>
  );
}
